import React, { useCallback, useEffect } from 'react';
import { Player } from '@lottiefiles/react-lottie-player';
import { t } from 'i18next';

import { useModuleSelector } from 'Pages/Courses/redux';
import UButton from 'Components/unit/UButton/UButton';
import UIAProgressBar from 'Components/unit/UIAProgressBar/UIAProgressBar';
import UIconButton from 'Components/unit/UIconButton/UIconButton';

import { AI_COURSE_VIEWS } from '..';
import { useModalState } from './hooks/useModalState';
import styles from './AILoading.styles';
import useAICourseView from '../../hooks/useAICourseView';

// Time to wait after the progress bar completion ends to trigger the navigation to the next page.
export const WAIT_TIME_ON_FINISH = 1500;

type AILoadingContentTypes = 'microknowledges' | 'course';
export type AILoadingSates = 'IDLE' | 'IS_LOADING' | 'IS_FINISHED' | 'GOTO_NEXT_VIEW';

export type AILoadingProps = {
  contentType: AILoadingContentTypes;
};

export const AILoading: React.FC<AILoadingProps> = ({ contentType }) => {
  const { showWarning, toggleModal, beforeUnloadHandler } = useModalState();

  const loadingState = useModuleSelector((state) => state.aiEdition.generationLoadingState);

  const strings = {
    subtitle: t('courses:course_loading.subtitle'),
    description: t('courses:course_loading.description'),
    loadingMicroknowledges: t('courses:course_loading.loadingMicroknowledges'),
    loadingCourse: t('courses:course_loading.loadingCourse'),
    disclaimer: t('courses:course_loading.disclaimer'),
    cancel: t('courses:course_loading.cancel'),

    modalTitle: t('courses:course_loading.modalTitle'),
    modalText: t('courses:course_loading.modalText'),
    modalButtonBack: t('courses:course_loading.modalButtonBack'),
    modalButtonCancel: t('courses:course_loading.modalButtonCancel'),
  };

  const { setViewType } = useAICourseView();

  const goToNextPage = useCallback(() => {
    window.onbeforeunload = null;
    switch (contentType) {
      case 'course':
        setViewType(AI_COURSE_VIEWS.PLAN_VALIDATION);
        break;
      case 'microknowledges':
        setViewType(AI_COURSE_VIEWS.MICROKNOWLEDGES_VALIDATION);
        break;
      default:
        setViewType(AI_COURSE_VIEWS.INITIAL);
        break;
    }
  }, [setViewType]);

  const goToPreviousPage = useCallback(() => {
    window.onbeforeunload = null;

    switch (contentType) {
      case 'course':
        return setViewType(AI_COURSE_VIEWS.INITIAL);
      case 'microknowledges':
        return setViewType(AI_COURSE_VIEWS.PLAN_VALIDATION);
      default:
        return setViewType(AI_COURSE_VIEWS.INITIAL);
    }
  }, []);

  useEffect(() => {
    if (!showWarning) {
      window.onbeforeunload = beforeUnloadHandler;
      return () => {
        window.onbeforeunload = null;
      };
    }
    if (showWarning) {
      window.onbeforeunload = null;
      return;
    }
  }, [showWarning]);

  useEffect(() => {
    if (showWarning) return; // prevent the page to change when the generation finishes while the modal is opened
    if (loadingState !== 'IS_FINISHED') return; // prevent the page to change if the generation is no yet done
    const timerFd = setTimeout(() => goToNextPage(), WAIT_TIME_ON_FINISH); // go to next page after a delay when generation is done
    return () => clearTimeout(timerFd);
  }, [loadingState, showWarning]);

  const dimmedStyle = { ...styles.contentMiddle, ...styles.contentDimmed };

  return (
    <div style={styles.wrapper}>
      <div id="page-content" style={styles.content}>
        {
          <div style={showWarning ? dimmedStyle : styles['contentMiddle']}>
            <div id="page-content_middle" style={styles.contentMiddle}>
              <Player
                src="../../../../../assets/animations/sparklesWithGradient.json"
                loop
                autoplay
                style={styles.sparklesAnimation}
              />
              <div style={styles.subtitle}>{strings.subtitle}</div>
              <div style={styles.description}>
                <span>{strings.description.split('\n')[0]}</span>
                <br />
                <span>{strings.description.split('\n')[1]}</span>
              </div>
              <UIAProgressBar isLoading={loadingState === 'IS_LOADING'} isFinished={loadingState === 'IS_FINISHED'} />
              {contentType === 'microknowledges' && (
                <div style={styles.loadingText}>{strings.loadingMicroknowledges}</div>
              )}
              {contentType === 'course' && <div style={styles.loadingText}>{strings.loadingCourse}</div>}
            </div>
            <div>
              <UButton leftIcon="close-light" text={strings.cancel} onClick={toggleModal} />
            </div>
            <div id="page-content_bottom" style={styles.contentBottom}>
              {contentType === 'microknowledges' && <div style={styles.disclamer}>{strings.disclaimer}</div>}
            </div>
          </div>
        }

        {showWarning && (
          <div id="warning-modal" style={styles.modalBackground}>
            <div id="modal-card" style={styles.modalCard}>
              <div id="modal-header" style={styles.modalHeader}>
                <UIconButton
                  style={styles.modalButtonClose}
                  icon="close"
                  size={'M'}
                  onClick={toggleModal}
                  ghost={true}
                />
                <div style={styles.modalTitle}>{strings.modalTitle}</div>
              </div>
              <div style={styles.modalBody}>
                <div id="modal-text" style={styles.modalText}>
                  {strings.modalText}
                </div>
              </div>
              <div id="modal-footer" style={styles.modalFooter}>
                <UButton text={strings.modalButtonBack} onClick={toggleModal} />
                <UButton type="destructive" text={strings.modalButtonCancel} onClick={goToPreviousPage} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AILoading;
